import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from 'api/services/dashboardService';
import type {
  PendingTimesheetWidget, SubmittedComparisonWidget, HoursVsMinimumWidget,
  EngagementStatusWidget, WipSummaryWidget,
} from 'api/types/report.types';

// ---- Pending Timesheets Widget ----
export const usePendingTimesheets = (managerId?: string, weekStartDate?: string) => {
  const [data, setData] = useState<PendingTimesheetWidget | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await dashboardService.getPendingTimesheets(managerId, weekStartDate)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [managerId, weekStartDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

// ---- Submitted Comparison Widget ----
export const useSubmittedComparison = (managerId?: string, weekStartDate?: string) => {
  const [data, setData] = useState<SubmittedComparisonWidget | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await dashboardService.getSubmittedComparison(managerId, weekStartDate)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [managerId, weekStartDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

// ---- Hours vs Minimum Widget ----
export const useHoursVsMinimum = (managerId?: string, weekStartDate?: string) => {
  const [data, setData] = useState<HoursVsMinimumWidget | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await dashboardService.getHoursVsMinimum(managerId, weekStartDate)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [managerId, weekStartDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

// ---- Engagement Status Widget ----
export const useEngagementStatus = (managerId?: string) => {
  const [data, setData] = useState<EngagementStatusWidget | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await dashboardService.getEngagementStatus(managerId)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [managerId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

// ---- Chargeable Cost (period) Widget — repurposed from the old WIP summary ----
export const useWipSummaryWidget = (
  managerId?: string,
  groupBy: string = 'Client',
  fromDate?: string,
  toDate?: string,
) => {
  const [data, setData] = useState<WipSummaryWidget | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await dashboardService.getWipSummary(managerId, groupBy, fromDate, toDate)); }
    catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [managerId, groupBy, fromDate, toDate]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};
